import { useCallback, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Sparkles } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import { useSecondaryNavActions, useSecondaryNavTitle } from "@client/components/secondary-nav";
import {
  ListFilterSelect,
  ListPageHeaderActions,
  ListSearchInput,
} from "@client/components/common/list-page-controls";
import { ClassificationFieldDialog } from "@client/components/semantic-platform/data-governance/classification-field-dialog";
import {
  ClassificationFieldStatusEnum,
  ClassificationSubjectTypeEnum,
} from "@tubo/api-models/business/platform/metadata-governance.enums";
import { useListClassificationFields } from "@tubo/api-models/hooks/classification-governance-api.hooks";
import type { ClassificationFieldResp } from "@tubo/api-models/hooks/classification-governance-api.schemas";
import { useValueVocabularyList } from "@tubo/api-models/hooks/value-vocabulary-api.hooks";
import { NS_CONTEXT_ALL, NS_CONTEXT_GLOBAL } from "@tubo/semantic-concepts";
import { useSemanticNamespaceList } from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";
import { useUiTranslation } from "@tubo/ui/i18n";

export const Route = createFileRoute("/_app/semantic-platform/tag-center/fields/")({
  // 领域上下文入 URL（分类字段经 vocabularyCode→数据字典 派生领域过滤）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: TagFieldsPage,
});

const SUBJECT_LABEL_KEYS: Record<string, string> = {
  file: "file",
  kbDoc: "kbDoc",
  modelEntity: "modelEntity",
  fact: "fact",
};

const PAGE_SIZE = 20;

/** 新建对话框的模式：空白表单 或 从数据字典新建（两步向导第一步选字典） */
type CreateMode = "blank" | "fromVocab";

function TagFieldsPage() {
  const { t } = useUiTranslation("semanticPlatform");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState<CreateMode>("blank");
  const [editing, setEditing] = useState<ClassificationFieldResp | null>(null);
  const [inputValue, setInputValue] = useState("");
  const isComposing = useRef(false);
  const handleSearchConfirm = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);
  // 领域上下文（经 vocabularyCode→数据字典 派生领域过滤；__all__ 不传=全部）
  const { nsFilter, setNs } = useNsContext();
  const statusFilterOptions = useMemo(
    () => [
      { value: "all", label: t("tagCenter.fields.statusFilters.all") },
      { value: ClassificationFieldStatusEnum.Active, label: t("tagCenter.fields.statusFilters.active") },
      { value: ClassificationFieldStatusEnum.Draft, label: t("tagCenter.fields.statusFilters.draft") },
      { value: ClassificationFieldStatusEnum.Disabled, label: t("tagCenter.fields.statusFilters.disabled") },
    ],
    [t],
  );
  const subjectFilterOptions = useMemo(
    () => [
      { value: "all", label: t("tagCenter.fields.subjectFilters.all") },
      ...Object.values(ClassificationSubjectTypeEnum).map((value) => ({
        value,
        label: t("tagCenter.fields.subjectFilters.filter", {
          subject: SUBJECT_LABEL_KEYS[value] ? t(`tagCenter.fields.subjects.${SUBJECT_LABEL_KEYS[value]}`) : value,
        }),
      })),
    ],
    [t],
  );
  const { data: vocabularies = [] } = useValueVocabularyList(undefined, { query: {} });
  const { data: namespaces = [] } = useSemanticNamespaceList({ query: {} });
  const nsNameById = useMemo(() => new Map(namespaces.map((ns) => [ns.id, ns.name])), [namespaces]);
  const vocabNsById = useMemo(() => new Map(vocabularies.map((v) => [v.code, v.namespaceId ?? null])), [vocabularies]);
  const { data = [], isLoading } = useListClassificationFields(
    {
      ...(search ? { search } : {}),
      ...(nsFilter !== NS_CONTEXT_ALL ? { ns: nsFilter } : {}),
    },
    { query: {} },
  );

  const filteredFields = useMemo(
    () => data.filter((field) => {
      const matchesStatus = statusFilter === "all" || field.status === statusFilter;
      const matchesSubject = subjectFilter === "all" || field.subjectTypes?.includes(subjectFilter);
      return matchesStatus && matchesSubject;
    }),
    [data, statusFilter, subjectFilter],
  );
  const pageCount = Math.max(1, Math.ceil(filteredFields.length / pageSize));
  const visibleFields = filteredFields.slice((Math.min(page, pageCount) - 1) * pageSize, Math.min(page, pageCount) * pageSize);
  const openedCountByVocabCode = useMemo(() => {
    const counts = new Map<string, number>();
    for (const field of data) {
      counts.set(field.vocabularyCode, (counts.get(field.vocabularyCode) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  useSecondaryNavTitle(useMemo(() => <span className="text-sm font-semibold">{t("tagCenter.fields.title")}</span>, [t]));
  useSecondaryNavActions(useMemo(() => (
    <ListPageHeaderActions>
      <ListFilterSelect
        items={statusFilterOptions}
        value={statusFilter}
        onValueChange={(value) => { setStatusFilter(value ?? "all"); setPage(1); }}
      />
      <ListFilterSelect
        items={subjectFilterOptions}
        value={subjectFilter}
        onValueChange={(value) => { setSubjectFilter(value ?? "all"); setPage(1); }}
      />
      <ListSearchInput
        placeholder={t("tagCenter.fields.searchPlaceholder")}
        className="w-56"
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          if (isComposing.current) return;
          handleSearchConfirm(event.target.value);
        }}
        onCompositionStart={() => { isComposing.current = true; }}
        onCompositionEnd={(event) => {
          isComposing.current = false;
          handleSearchConfirm(event.currentTarget.value);
        }}
        onBlur={(event) => {
          if (!isComposing.current) handleSearchConfirm(event.currentTarget.value);
        }}
      />
      <Button variant="outline" size="sm" onClick={() => { setCreateMode("fromVocab"); setCreateOpen(true); }}>
        <Sparkles className="mr-1 size-4" />{t("tagCenter.fields.createFromVocabulary")}
      </Button>
      <Button size="sm" onClick={() => { setCreateMode("blank"); setCreateOpen(true); }}>
        <Plus className="mr-1 size-4" />{t("tagCenter.fields.create")}
      </Button>
    </ListPageHeaderActions>
  ), [inputValue, statusFilter, subjectFilter, setCreateOpen, handleSearchConfirm, statusFilterOptions, subjectFilterOptions, t]));

  const columns = useMemo<DataTableColumn<ClassificationFieldResp>[]>(
    () => [
    { id: "name", header: t("tagCenter.fields.name"), cell: (row) => <div><div className="font-medium">{row.name}</div><code className="text-xs text-muted-foreground">{row.code}</code></div> },
    { id: "vocabulary", header: t("tagCenter.fields.vocabulary"), cell: (row) => <code className="text-xs">{row.vocabularyCode}</code> },
    { id: "domain", header: t("tagCenter.fields.domain"), cell: (row) => { const nsId = vocabNsById.get(row.vocabularyCode); const name = nsId ? nsNameById.get(nsId) : undefined; return name ? <Badge variant="outline">{name}</Badge> : <span className="text-xs text-muted-foreground">{t("tagCenter.fields.global")}</span>; } },
    { id: "subjects", header: t("tagCenter.fields.applicableSubjects"), cell: (row) => <div className="flex flex-wrap gap-1">{(row.subjectTypes ?? []).map((subject) => <Badge key={subject} variant="outline">{SUBJECT_LABEL_KEYS[subject] ? t(`tagCenter.fields.subjects.${SUBJECT_LABEL_KEYS[subject]}`) : subject}</Badge>)}</div> },
    { id: "scope", header: t("tagCenter.fields.scope"), cell: (row) => row.contextMode === "kbScoped" ? t("tagCenter.fields.kbContext") : t("tagCenter.fields.global") },
    { id: "cardinality", header: t("tagCenter.fields.valueSelection"), cell: (row) => row.cardinality === "multiple" ? t("tagCenter.fields.multiple") : t("tagCenter.fields.single") },
    { id: "review", header: t("tagCenter.fields.review"), cell: (row) => t(`tagCenter.fields.reviewPolicies.${row.reviewPolicy}`, { defaultValue: row.reviewPolicy }) },
    { id: "status", header: t("tagCenter.fields.status"), cell: (row) => <Badge variant={row.status === "active" ? "secondary" : "outline"}>{row.status === "active" ? t("tagCenter.fields.enabled") : row.status === "draft" ? t("tagCenter.fields.draft") : t("tagCenter.fields.disabled")}</Badge> },
    { id: "actions", header: t("tagCenter.fields.actions"), cell: (row) => <Button variant="ghost" size="icon-sm" aria-label={t("tagCenter.fields.edit", { name: row.name })} onClick={() => setEditing(row)}><Pencil className="size-4" /></Button> },
    ],
    [nsNameById, vocabNsById, t],
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <DataTable
        data={visibleFields}
        columns={columns}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        page={Math.min(page, pageCount)}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={filteredFields.length}
        onPageChange={setPage}
        emptyState={<div className="py-10 text-center text-sm text-muted-foreground"><p>{t("tagCenter.fields.emptyTitle")}</p><p className="mt-1 text-xs">{t("tagCenter.fields.emptyDescription")}</p></div>}
      />
      {/* 新建（空白 / 从数据字典两步向导）与编辑共用本组件 */}
      <ClassificationFieldDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        pickVocabulary={createMode === "fromVocab"}
        vocabularies={vocabularies}
        openedCountByVocabCode={openedCountByVocabCode}
        onVocabularyPicked={(vocab) => {
          // 字段跟随数据字典领域：选定字典即切换上下文，创建后立即可见（全局字典回全局）
          setNs(vocab.namespaceId ?? NS_CONTEXT_GLOBAL);
        }}
      />
      <ClassificationFieldDialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)} editing={editing} />
    </div>
  );
}
